import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Sparkles, FileText, Clock, TrendingUp } from 'lucide-react';

const DashboardPage: React.FC = () => {
  const { user } = useAuth();

  if (!user) return null;

  const usagePercentage = (user.generationsUsed / user.generationLimit) * 100;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">Welcome back, {user.name}!</h1>

      <div className="grid md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Current Plan</h3>
            <Sparkles className="h-6 w-6 text-blue-600" />
          </div>
          <p className="text-2xl font-bold text-blue-600">{user.subscriptionTier.toUpperCase()}</p>
          {user.subscriptionTier === 'free' && (
            <Link to="/pricing" className="text-sm text-blue-600 hover:underline mt-2 inline-block">
              Upgrade for more features
            </Link>
          )}
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Monthly Usage</h3>
            <FileText className="h-6 w-6 text-green-600" />
          </div>
          <p className="text-2xl font-bold">
            {user.generationsUsed} / {user.generationLimit}
          </p>
          <div className="mt-2 bg-gray-200 rounded-full h-2">
            <div
              className="bg-green-600 h-2 rounded-full"
              style={{ width: `${Math.min(usagePercentage, 100)}%` }}
            />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Quick Actions</h3>
            <TrendingUp className="h-6 w-6 text-purple-600" />
          </div>
          <div className="space-y-2">
            <Link
              to="/generate"
              className="block text-center bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              Generate Content
            </Link>
            <Link
              to="/history"
              className="block text-center bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300"
            >
              View History
            </Link>
          </div>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-xl font-semibold mb-4">Getting Started</h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="border-l-4 border-blue-600 pl-4">
            <h3 className="font-semibold mb-1">1. Choose Your Content Type</h3>
            <p className="text-gray-600">Select from blog posts, social media, emails, or product descriptions</p>
          </div>
          <div className="border-l-4 border-green-600 pl-4">
            <h3 className="font-semibold mb-1">2. Describe Your Topic</h3>
            <p className="text-gray-600">Tell our AI what you want to write about</p>
          </div>
          <div className="border-l-4 border-purple-600 pl-4">
            <h3 className="font-semibold mb-1">3. Generate & Customize</h3>
            <p className="text-gray-600">Get your content in seconds and edit as needed</p>
          </div>
          <div className="border-l-4 border-orange-600 pl-4">
            <h3 className="font-semibold mb-1">4. Export & Use</h3>
            <p className="text-gray-600">Copy your content and use it anywhere</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;